/**
 * @author        matt@zumper.com (Matthieu Devlin)
 * @copyright     Copyright (c) 2018, Zumper
 * @description
 */

import {
  selectActiveListings,
  selectAllListings,
  selectInactiveListings,
  selectIsAddressOnBuilding,
  selectIsSingleUnit,
} from '@zcode-ui/web-zumper-global-state'
import type { TopListable } from '@zcode/bizos'
import type {
  Listable,
  MaybeEmptyProperty,
  Pin,
  RawPinInventorySearch,
  State,
} from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import { isNumber } from '@zcode/utils'
import { getTraktorEntityId } from './getTracktorEntityId'

/**
 * Gets the event properties for an entity (listing, building, rentable, pin, cluster, etc)
 *
 * @param entity
 */
export const getEntityProperties = (
  entity: MaybeEmpty<Listable> | MaybeEmptyProperty | Pin<RawPinInventorySearch> | TopListable,
  state: State
) => {
  const {
    city,
    bathrooms,
    bedrooms,
    features,
    feedName,
    imageIds: entityImageIds,
    images,
    isBuilding,
    lat,
    lng,
    maxBathrooms,
    maxBedrooms,
    minBathrooms,
    minBedrooms,
    state: entityState,
  } = entity
  const imageIds = entityImageIds ?? images?.map((image) => image.id)
  const isAddressOnBuilding = selectIsAddressOnBuilding(state)
  const isAddress = !!selectAllListings(state)?.length
  const isSingleUnitAddress = selectIsSingleUnit(state)

  const activeListings: number[] = []
  const inactiveListings: number[] = []

  if ((isAddress || isAddressOnBuilding) && !isSingleUnitAddress) {
    const activeAddressListings = selectActiveListings(state)
    const inactiveAddressListings = selectInactiveListings(state)

    activeAddressListings.map(({ listingId }) => {
      activeListings.push(listingId)
    })

    inactiveAddressListings.map(({ listingId }) => {
      inactiveListings.push(listingId)
    })
  } else if (isBuilding) {
    for (const { isActive, listingId } of entity.floorplans ?? []) {
      if (isActive) {
        activeListings.push(listingId)
      } else {
        inactiveListings.push(listingId)
      }
    }
  }

  return {
    propertyId: getTraktorEntityId(entity),
    minBeds: isNumber(minBedrooms) ? minBedrooms : bedrooms,
    maxBeds: isNumber(maxBedrooms) ? maxBedrooms : bedrooms,
    minBaths: isNumber(minBathrooms) ? minBathrooms : bathrooms,
    maxBaths: isNumber(maxBathrooms) ? maxBathrooms : bathrooms,
    imageCount: imageIds ? imageIds.length : 0,
    imageIds,
    city,
    state: entityState,
    lat,
    lng,
    features,
    feedName,
    ...(activeListings.length > 0 && { activeListings: activeListings }),
    ...(inactiveListings.length > 0 && { inactiveListings: inactiveListings }),
  }
}
